import { ColDef, GridOptions } from '@ag-grid-community/core';
import AgGridSolid from '@ag-grid-community/solid';
import { atgLogoSVG } from '@assets';
import {
  Box,
  ConfirmationDialogBox,
  Divider,
  Grid,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { ClipboardCheckIcon } from '@atgfr8/icons';
import { SelectInput } from '@components';
import { Button } from '@components/Button';
import { alternateRowStyle } from '@components/Grid/utils';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  VendorSearchResult,
  VendorSearchServiceTypeResult,
} from '@store/vendors/vendorDetails';
import { Accessor, Component, createSignal, Show } from 'solid-js';

import { VendorSelect_DialogBox_Id } from './constants';

type VendorSearchResultGridOnLoadProps = {
  vendorData: Accessor<VendorSearchResult[]>;
  isLoading: Accessor<boolean>;
  onVendorSelect?: (
    vendor: VendorSearchResult,
    serviceType?: VendorSearchServiceTypeResult,
  ) => void;
  savedVendorSelected?: boolean;
  formOpen?: boolean;
  vendorName?: string;
};

export const VendorSearchResultGridOnLoad: Component<
  VendorSearchResultGridOnLoadProps
> = (props) => {
  const [selectedName, setSelectedName] = createSignal<string | null>(null);
  const [selectedVendor, setSelectedVendor] =
    createSignal<VendorSearchResult | null>(null);
  const [selectedServiceType, setSelectedServiceType] =
    createSignal<VendorSearchServiceTypeResult | null>(null);
  const [selectedServiceTypes, setSelectedServiceTypes] = createSignal<
    VendorSearchServiceTypeResult[]
  >([]);
  const replaceSelectedVendorText = () => {
    return (
      <Typography
        variant="body2"
        component="p"
        class="font-sm! font-roboto! text-[#000000e3]!"
      >
        You are about to replace {props.vendorName ?? 'the selected vendor'}{' '}
        with {selectedName() ?? 'a new vendor'}. All vendor details will be
        updated accordingly.
        <Box class="mt-3">
          <SelectInput
            label="Available Service Type(s)"
            menuItems={selectedServiceTypes().map((service) => ({
              label: service.name,
              value: service.name,
            }))}
            value={selectedServiceType()?.name || ''}
            onChange={(value) => {
              setSelectedServiceType(
                selectedServiceTypes().find(
                  (service) => service.name == value,
                ) || null,
              );
            }}
            dataTestId="vendor-replace-service-type-select"
          />
          <Typography
            variant="body2"
            component="p"
            class="text-xs! text-[#026EA1]!"
          >
            Select a service type for this location. If you don't see the one
            you need, add it to this location on the vendor profile.
          </Typography>
        </Box>
      </Typography>
    );
  };
  const replaceNewVendorText = () => {
    return (
      <Typography
        variant="body2"
        component="p"
        class="font-sm! font-roboto! text-[#000000e3]!"
      >
        You are about to select {selectedName() ?? 'a new vendor'}. Any vendor
        details you've entered that have not been saved will be cleared and
        replaced with this vendor's profile.
        <br />
        <br />
        Continuing will not create a new vendor profile from the information
        already entered.
        <Box class="mt-3">
          <SelectInput
            label="Available Service Type(s)"
            menuItems={selectedServiceTypes().map((service) => ({
              label: service.name,
              value: service.name,
            }))}
            value={selectedServiceType()?.name || ''}
            onChange={(value) => {
              setSelectedServiceType(
                selectedServiceTypes().find(
                  (service) => service.name == value,
                ) || null,
              );
            }}
            dataTestId="vendor-new-vendor-service-type-select"
          />
          <Typography
            variant="body2"
            component="p"
            class="text-xs! text-[#026EA1]!"
          >
            Select a service type for this location. If you don't see the one
            you need, add it to this location on the vendor profile.
          </Typography>
        </Box>
      </Typography>
    );
  };

  const columnsDefs: ColDef[] = [
    {
      headerName: 'Vendor Name',
      minWidth: 150,
      cellRenderer: (params: { data: VendorSearchResult }) => {
        return (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            onClick={() => {
              window.open(`/vendors/details/${params.data.vendorId}`, '_blank');
            }}
          >
            <Show when={params.data.atgVerified}>
              <ClipboardCheckIcon class="mr-1! text-[#248670]! align-middle!" />
            </Show>
            <Typography
              variant="body2"
              component="span"
              class="align-middle! underline! text-[#026EA1]! font-roboto! text-sm! cursor-pointer!"
            >
              {params.data.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      headerName: 'Service Types',
      field: 'serviceTypes',
      minWidth: 160,
      valueGetter: (params: { data: VendorSearchResult }) => {
        return (
          params.data.serviceTypes
            .map((serviceType) => serviceType.name)
            .join(', ') || ''
        );
      },
    },
    {
      headerName: 'Operating Address',
      field: 'addressLine1',
      minWidth: 150,
    },
    {
      headerName: 'City',
      field: 'city',
      minWidth: 120,
    },
    {
      headerName: 'State',
      field: 'state',
      minWidth: 90,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      minWidth: 170,
      cellRenderer: (params: { data: VendorSearchResult }) => {
        return (
          <Button
            class="bg-[#026EA1]! text-white!"
            variant="text"
            label={
              props.savedVendorSelected ? 'Replace Vendor' : 'Select Vendor'
            }
            onClick={() => {
              if (!props.savedVendorSelected && !props.formOpen) {
                props.onVendorSelect?.(params.data);
              } else {
                setSelectedVendor(params.data);
                setSelectedName(params.data.name);
                setSelectedServiceTypes(params.data.serviceTypes);
                openDialogBox(VendorSelect_DialogBox_Id);
              }
            }}
            dataTestId="vendors-select-vendor-button"
          />
        );
      },
    },
  ];

  const gridOptions: GridOptions = {
    defaultColDef: {
      flex: 1,
      sortable: false,
      filter: false,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        padding: '5px 10px',
        'word-break': 'break-word',
        'white-space': 'normal',
        'line-height': '1.6 ',
        'align-items': 'center !important',
      },
    },
    getRowStyle: alternateRowStyle,
    sideBar: {
      toolPanels: [],
      defaultToolPanel: '',
    },
  };

  return (
    <>
      <Grid item size={12}>
        <Divider />
      </Grid>
      <Grid item size={12}>
        <Typography
          variant="body1"
          component="p"
          class="text-base! font-roboto! font-medium! pb-2 text-[#123B50]!"
        >
          Vendor Results
        </Typography>
        <Box
          class="ag-theme-alpine ag-theme-custom-border my-row-group-border custom-ag-row-icon-grid flex-1!"
          style={{ height: '293px', width: '100%' }}
        >
          <AgGridSolid
            overlayNoRowsTemplate="No data available"
            overlayLoadingTemplate={atgLogoSVG}
            class="no-cell-grid"
            columnDefs={columnsDefs}
            rowData={props.vendorData()}
            gridOptions={gridOptions}
          />
        </Box>
      </Grid>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(VendorSelect_DialogBox_Id)}
        dataTestId={VendorSelect_DialogBox_Id}
        id={VendorSelect_DialogBox_Id}
        onSubmitText={
          props.savedVendorSelected ? 'Replace Vendor' : 'Use Selected Vendor'
        }
        title={
          (
            <Typography
              variant="h6"
              component="div"
              class="font-roboto! font-medium! text-[#123B50]!"
            >
              {props.savedVendorSelected
                ? 'Replace current vendor?'
                : 'Replace new vendor details?'}
            </Typography>
          ) as unknown as string
        }
        onSubmit={() => {
          const vendor = selectedVendor();
          if (props.onVendorSelect && vendor !== null) {
            props.onVendorSelect(vendor, selectedServiceType() || undefined);
          }
          setSelectedServiceTypes([]);
          setSelectedVendor(null);
          closeDialogBox(VendorSelect_DialogBox_Id);
        }}
        onCancel={() => {
          closeDialogBox(VendorSelect_DialogBox_Id);
        }}
        closeDialogBox={(_reason) => {
          closeDialogBox(VendorSelect_DialogBox_Id);
        }}
      >
        {props.savedVendorSelected
          ? replaceSelectedVendorText()
          : replaceNewVendorText()}
      </ConfirmationDialogBox>
    </>
  );
};
